/**
 * @file: useObdDevices.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { useServiceFactory } from '@euler/services/factory';
import { usePersistFn, useRefreshableAsyncResource } from '@euler/utils/hooks';
import produce from 'immer';

export const useObdDevices = () => {
  const { storeService } = useServiceFactory();
  const key = 'store:obd-devices';
  const fetch = usePersistFn(async () => {
    return await storeService.listObdDevices();
  });

  const {
    value: devices,
    update,
    refresh,
    isRefreshing,
  } = useRefreshableAsyncResource(fetch, key);

  const updateAsync = usePersistFn(
    async (
      id: string,
      params: {
        serialNo?: string;
        name?: string;
      },
    ) => {
      const updated = await storeService.updateObdDevice(id, params);
      const result = produce(devices, draft => {
        const index = draft.findIndex(x => x.id === id);
        if (index < 0) {
          console.warn(
            'cannot find obd device by id: ',
            id,
            ', this must be an error',
          );
          return;
        }
        draft[index] = updated;
      });
      update(result);
    },
  );

  const createAsync = usePersistFn(
    async (params: { serialNo: string; name: string; remark?: string }) => {
      const created = await storeService.registerObdDevice(params);
      const result = produce(devices, draft => {
        draft.push(created);
      });
      update(result);
    },
  );

  const deleteAsync = usePersistFn(async (id: string) => {
    await storeService.removeObdDevice(id);
    const result = produce(devices, draft => {
      const index = draft.findIndex(x => x.id === id);
      if (index < 0) {
        console.warn(
          'cannot find obd device by id: ',
          id,
          ', this must be an error',
        );
        return;
      }
      draft.splice(index, 1);
    });
    update(result);
  });

  return {
    devices,
    refresh,
    isRefreshing,
    createAsync,
    updateAsync,
    deleteAsync,
  };
};

export const useObdDeviceCount = () => {
  const { devices } = useObdDevices();
  return devices.length;
};
